import React, { useCallback, useMemo, useState } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAppDispatch, useAppSelector } from "@/hooks/reduxToolkitHook"
import CreditDemandItem from "components/transactions/credit-demands/CreditDemandItem"
import { rejectTransaction, validateTransaction } from "@/redux/features/transactionsSlice"
import { WalletTransaction } from "@/types/TransactionsType"
import { AlertCircle, CheckCircle, Clock, Layers, Search } from "lucide-react"
import TransactionDetailSheet from "components/transactions/TransactionDetailSheet"
import CreditDemandValidationModal from "components/transactions/credit-demands/CreditDemandValidationModal"
import CreditDemandRejectionModal from "components/transactions/credit-demands/CreditDemandRejectionModal"
import CompanyFilter from "components/transactions/credit-demands/CompanyFilter"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Input } from "@/components/ui/input"

type Filter = "ALL" | "IN_PROGRESS" | "PENDING" | "VALIDATED" | "REJECTED"

const ITEMS_PER_PAGE = 5

const CreditDemands = () => {
  const dispatch = useAppDispatch()
  const { transactions } = useAppSelector(state => state.transactions)

  const [filter, setFilter] = useState<Filter>("PENDING")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)

  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())
  const [openPreview, setOpenPreview] = useState(false)
  const [openValidateModal, setOpenValidateModal] = useState(false)
  const [openRejectModal, setOpenRejectModal] = useState(false)

  const [processingTransaction, setProcessingTransaction] = useState<WalletTransaction | null>(null)

  // ── All requested transactions ────────────────────────────────────────────
  const pendingRequests = useMemo(() => {
    return transactions.filter((item: WalletTransaction) => item.is_requested)
  }, [transactions])

  // ── Company options for the combobox ─────────────────────────────────────
  const companyOptions = useMemo(() => {
    return Array.from(
      new Set(
        pendingRequests
          .map((t: WalletTransaction) => t.company_name)
          .filter(Boolean),
      ),
    ) as string[]
  }, [pendingRequests])

  // ── Filtered by tab + selected companies ─────────────────────────────────
  const filteredRequests = useMemo(() => {
    if (!pendingRequests.length) return []

    return pendingRequests.filter((request: WalletTransaction) => {
      const matchesStatus = (() => {
        switch (filter) {
          case "PENDING":
            return request.status === "PENDING"
          case "VALIDATED":
            return request.status === "VALIDATED"
          case "REJECTED":
            return request.status === "REJECTED"
          default:
            return true
        }
      })()

      const matchesCompany =
        selectedCompanies.length === 0 ||
        selectedCompanies.includes(request.company_name)

      const q = searchQuery.toLowerCase().trim()
      const matchesSearch = !q || (
        request.reference?.toLowerCase().includes(q) ||
        request.initiator_email?.toLowerCase().includes(q) ||
        request.initiator_name?.toLowerCase().includes(q)
      )

      return matchesStatus && matchesCompany && matchesSearch
    })
  }, [pendingRequests, filter, selectedCompanies, searchQuery])

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE)

  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredRequests.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredRequests, currentPage])

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }

  const handleFilterChange = (value: string) => {
    setFilter(value as Filter)
    setCurrentPage(1)
  }

  const handleCompanyChange = (values: string[]) => {
    setSelectedCompanies(values)
    setCurrentPage(1)
  }

  // ── Page numbers with ellipsis ────────────────────────────────────────────
  const getPageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = []
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push("ellipsis")
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i)
      }
      if (currentPage < totalPages - 2) pages.push("ellipsis")
      pages.push(totalPages)
    }
    return pages
  }

  // ── Tab counts (respect company filter) ──────────────────────────────────
  const getTabCount = useCallback((status: Filter): number => {
    const companyFiltered = selectedCompanies.length === 0
      ? pendingRequests
      : pendingRequests.filter((r: WalletTransaction) =>
        selectedCompanies.includes(r.company_name),
      )

    if (status === "ALL") return companyFiltered.length
    return companyFiltered.filter((r: WalletTransaction) => r.status === status).length
  }, [pendingRequests, selectedCompanies])

  // ─────────────────────────────────────────────────────────────────────────

  const itemsColorHighlight = useMemo(() => {
    switch (filter) {
      case "PENDING":
        return "gray"
      case "VALIDATED":
        return "blue"
      case "REJECTED":
        return "red"
      default:
        return "purple"
    }
  }, [filter])

  const handleValidate = useCallback(async (id: string, action: string) => {
    setProcessingIds(prev => new Set(prev).add(id))
    try {
      await dispatch(validateTransaction({ id, action })).unwrap()
    } catch (error) {
      console.error("Erreur validation", error)
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }
  }, [dispatch])

  const handleReject = useCallback(async (id: string, action: string, reason: string) => {
    setProcessingIds(prev => new Set(prev).add(id))
    try {
      await dispatch(rejectTransaction({ id, action, reason })).unwrap()
    } catch (error) {
      console.error("Erreur rejet", error)
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(id)
        return newSet
      })
    }
  }, [dispatch])

  const handleViewModalOpen = (transaction: WalletTransaction) => {
    setProcessingTransaction(transaction)
    setOpenPreview(true)
  }
  const handleRejectionModalOpen = (transaction: WalletTransaction) => {
    setProcessingTransaction(transaction)
    setOpenRejectModal(true)
  }
  const handleValidationModalOpen = (transaction: WalletTransaction) => {
    setProcessingTransaction(transaction)
    setOpenValidateModal(true)
  }

  const renderContent = () => {
    if (filteredRequests.length === 0) {
      const messages: Record<Filter, string> = {
        ALL: "Aucune demande",
        PENDING: "Aucune demande en attente",
        IN_PROGRESS: "Aucune demande en cours",
        VALIDATED: "Aucune demande validée",
        REJECTED: "Aucune demande rejetée",
      }

      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Clock className="h-12 w-12 mb-3 text-gray-300" />
          <p className="text-lg font-medium">{messages[filter]}</p>
          <p className="text-sm">Les nouvelles demandes apparaîtront ici</p>
        </div>
      )
    }

    return (
      <>
        {/* ── Items list ── */}
        <div className="space-y-3">
          {paginatedRequests.map(transaction => (
            <React.Fragment key={transaction.id}>
              <CreditDemandItem
                transaction={transaction}
                highlightItemsColor={itemsColorHighlight}
                onView={() => handleViewModalOpen(transaction)}
                onValidateStart={() => handleValidationModalOpen(transaction)}
                onRejectionStart={() => handleRejectionModalOpen(transaction)}
              />
            </React.Fragment>
          ))}
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-2 mt-6">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(currentPage - 1)}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>

                {getPageNumbers().map((page, index) => (
                  <PaginationItem key={index}>
                    {page === "ellipsis" ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        isActive={page === currentPage}
                        onClick={() => handlePageChange(page)}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(currentPage + 1)}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <p className="text-xs text-gray-400">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredRequests.length)} sur {filteredRequests.length} demandes
            </p>
          </div>
        )}

        {/* ── Modals ── */}
        {processingTransaction && (
          <>
            <TransactionDetailSheet
              transactionId={processingTransaction.id}
              sheetOpen={openPreview}
              onSheetOpenChange={setOpenPreview}
              onReject={() => handleRejectionModalOpen(processingTransaction!)}
              onValidate={() => handleValidationModalOpen(processingTransaction!)}
            />
            <CreditDemandValidationModal
              open={openValidateModal}
              onClose={() => setOpenValidateModal(false)}
              onValidate={() => {
                handleValidate(processingTransaction!.id, "approve").then(() => {
                  setProcessingTransaction(null)
                  setOpenValidateModal(false)
                })
              }}
              transaction={processingTransaction}
            />
            <CreditDemandRejectionModal
              open={openRejectModal}
              onClose={() => setOpenRejectModal(false)}
              onReject={(rejectComment) => {
                handleReject(processingTransaction!.id, "reject", rejectComment).then(() => {
                  setProcessingTransaction(null)
                  setOpenRejectModal(false)
                })
              }}
              transaction={processingTransaction}
            />
          </>
        )}
      </>
    )
  }

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm">

      {/* ── Company filter ── */}
      {companyOptions.length > 0 && (
        <div className="flex items-center gap-3">
          <CompanyFilter
            options={companyOptions}
            value={selectedCompanies}
            onChange={handleCompanyChange}
          />
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Référence, email, nom..."
              className="pl-9 w-72 rounded rounded-lg"
            />
          </div>
        </div>
      )}

      <Tabs value={filter} onValueChange={handleFilterChange} className="w-full">
        <TabsList className="w-full py-6">
          <div className="flex w-full gap-1">

            <TabsTrigger value="ALL" className="flex-1 py-2">
              <div className="flex items-center justify-center gap-2">
                <Layers className="h-4 w-4" />
                <span>Toutes</span>
                {getTabCount("ALL") > 0 && (
                  <span
                    className="bg-gray-200 text-gray-700 min-w-[1.5rem] h-5 flex items-center justify-center rounded-full text-xs font-semibold px-1">
                    {getTabCount("ALL")}
                  </span>
                )}
              </div>
            </TabsTrigger>

            <TabsTrigger value="PENDING" className="flex-1">
              <div className="flex items-center justify-center gap-2">
                <Clock className="h-4 w-4" />
                <span>En Attente</span>
                {getTabCount("PENDING") > 0 && (
                  <span
                    className="bg-blue-100 text-blue-700 px-2 min-w-[1.5rem] h-5 flex items-center justify-center rounded-full text-xs font-semibold">
                    {getTabCount("PENDING")}
                  </span>
                )}
              </div>
            </TabsTrigger>

            <TabsTrigger value="VALIDATED" className="flex-1">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>Validées</span>
                {getTabCount("VALIDATED") > 0 && (
                  <span
                    className="bg-green-100 text-green-700 px-2 min-w-[1.5rem] h-5 flex items-center justify-center rounded-full text-xs font-semibold">
                    {getTabCount("VALIDATED")}
                  </span>
                )}
              </div>
            </TabsTrigger>

            <TabsTrigger value="REJECTED" className="flex-1">
              <div className="flex items-center justify-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>Rejetées</span>
                {getTabCount("REJECTED") > 0 && (
                  <span
                    className="bg-red-100 text-red-700 px-2 min-w-[1.5rem] h-5 flex items-center justify-center rounded-full text-xs font-semibold">
                    {getTabCount("REJECTED")}
                  </span>
                )}
              </div>
            </TabsTrigger>

          </div>
        </TabsList>

        <TabsContent value={filter} className="mt-10">
          {renderContent()}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default CreditDemands